import { useState } from "preact/hooks";
import type { JSX } from "preact";
import { api } from "../../api";
import { useI18n } from "../../lib/i18n";
import { Section, SettingRow, StatusLine, useStatus } from "./common";

/** The operator's own password and the recovery codes that stand in for a lost phone. */
export function SecurityTab(): JSX.Element {
  const { t: all } = useI18n();
  const t = all.security;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [passwordStatus, showPassword, failPassword] = useStatus();

  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [codeStatus, , failCodes] = useStatus();

  const mismatch = again.length > 0 && next !== again;

  async function changePassword(event: Event): Promise<void> {
    event.preventDefault();
    try {
      await api.changePassword(current, next);
      setCurrent("");
      setNext("");
      setAgain("");
      showPassword("ok", t.changed);
    } catch (err) {
      failPassword(err);
    }
  }

  async function regenerate(event: Event): Promise<void> {
    event.preventDefault();
    try {
      const res = await api.regenerateRecoveryCodes(code);
      setCodes(res.recoveryCodes);
      setCode("");
    } catch (err) {
      failCodes(err);
    }
  }

  return (
    <>
      <Section title={t.passwordTitle} intro={t.passwordIntro}>
        <form onSubmit={(e) => void changePassword(e)}>
          <SettingRow title={t.current} htmlFor="pw-current">
            <input
              id="pw-current"
              type="password"
              autocomplete="current-password"
              value={current}
              onInput={(e) => setCurrent(e.currentTarget.value)}
            />
          </SettingRow>
          <SettingRow title={t.next} hint={t.nextHint} htmlFor="pw-next">
            <input
              id="pw-next"
              type="password"
              autocomplete="new-password"
              minLength={12}
              value={next}
              onInput={(e) => setNext(e.currentTarget.value)}
            />
          </SettingRow>
          <SettingRow title={t.again} htmlFor="pw-again">
            <div class="stack">
              <input
                id="pw-again"
                type="password"
                autocomplete="new-password"
                value={again}
                onInput={(e) => setAgain(e.currentTarget.value)}
              />
              {mismatch ? <p class="field-error">{t.mismatch}</p> : null}
            </div>
          </SettingRow>
          <div class="form-foot">
            <button class="btn primary" disabled={!current || next.length < 12 || next !== again}>
              {t.change}
            </button>
          </div>
        </form>
        <StatusLine status={passwordStatus} />
      </Section>

      <Section title={t.codesTitle} intro={t.codesIntro}>
        {codes ? (
          <>
            <div class="alert warn">{t.codesWarn}</div>
            <div class="codes">
              {codes.map((c) => (
                <code key={c}>{c}</code>
              ))}
            </div>
            <div class="form-foot">
              <button class="btn" onClick={() => setCodes(null)}>
                {t.codesDone}
              </button>
            </div>
          </>
        ) : (
          <SettingRow title={t.appCode} hint={t.appCodeHint} htmlFor="codes-app">
            <form onSubmit={(e) => void regenerate(e)} class="inline-actions">
              <input
                id="codes-app"
                type="text"
                inputMode="numeric"
                autocomplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={code}
                onInput={(e) => setCode(e.currentTarget.value.replace(/D/g, ""))}
              />
              <button class="btn" disabled={code.length !== 6}>
                {t.newCodes}
              </button>
            </form>
          </SettingRow>
        )}
        <StatusLine status={codeStatus} />
      </Section>
    </>
  );
}
